import { z } from "zod";

export const equipmentKindSchema = z.enum([
  "sip_phone",
  "ata",
  "gateway",
  "other",
]);
export type EquipmentKind = z.infer<typeof equipmentKindSchema>;
export type EquipmentModel = {
  id: string;
  brandId: string;
  manufacturer: string;
  model: string;
  kind: EquipmentKind;
  configurationFamily: string;
  note?: string;
};
export const equipmentBrands = [
  { id: "yealink", name: "Yealink" },
  { id: "grandstream", name: "Grandstream" },
  { id: "poly", name: "Poly / Polycom" },
  { id: "cisco", name: "Cisco" },
  { id: "fanvil", name: "Fanvil" },
  { id: "snom", name: "Snom" },
] as const;

function models(
  brandId: string,
  manufacturer: string,
  kind: EquipmentKind,
  configurationFamily: string,
  entries: [string, string][],
  note?: string,
): EquipmentModel[] {
  return entries.map(([suffix, model]) => ({
    id: `${brandId}-${suffix}`,
    brandId,
    manufacturer,
    model,
    kind,
    configurationFamily,
    ...(note ? { note } : {}),
  }));
}
export const equipmentCatalog: readonly EquipmentModel[] = [
  ...models("yealink", "Yealink", "sip_phone", "yealink-sip", [
    ["t33g", "SIP-T33G"],
    ["t46u", "SIP-T46U"],
    ["t54w", "SIP-T54W"],
    ["t57w", "SIP-T57W"],
  ]),
  ...models("grandstream", "Grandstream", "ata", "grandstream-ht8xx", [
    ["ht801", "HT801"],
    ["ht802", "HT802"],
  ]),
  ...models(
    "grandstream",
    "Grandstream",
    "ata",
    "grandstream-ht8xx-v2",
    [
      ["ht801-v2", "HT801 V2"],
      ["ht802-v2", "HT802 V2"],
    ],
    "Check the hardware label. Original and V2 models use different configurations.",
  ),
  ...models("grandstream", "Grandstream", "sip_phone", "grandstream-grp", [
    ["grp2612", "GRP2612"],
    ["grp2615", "GRP2615"],
  ]),
  ...models("grandstream", "Grandstream", "sip_phone", "grandstream-gxp", [
    ["gxp2170", "GXP2170"],
  ]),
  ...models(
    "poly",
    "Poly",
    "sip_phone",
    "poly-vvx-ucs",
    [
      ["vvx250-ucs", "VVX 250 (UCS)"],
      ["vvx350-ucs", "VVX 350 (UCS)"],
      ["vvx450-ucs", "VVX 450 (UCS)"],
    ],
    "Choose this only for UC Software (UCS), not OBi Edition or Teams firmware.",
  ),
  ...models("poly", "Poly", "sip_phone", "poly-edge-pvos", [
    ["edge-e220", "Edge E220"],
    ["edge-e350", "Edge E350"],
  ]),
  ...models(
    "cisco",
    "Cisco",
    "sip_phone",
    "cisco-mpp",
    [
      ["8841-mpp", "8841 (MPP / 3PCC)"],
      ["8851-mpp", "8851 (MPP / 3PCC)"],
      ["8861-mpp", "8861 (MPP / 3PCC)"],
    ],
    "Requires Multiplatform (MPP / 3PCC) firmware. For Enterprise firmware, choose Other model.",
  ),
  ...models(
    "fanvil",
    "Fanvil",
    "sip_phone",
    "fanvil-x",
    [
      ["x3u", "X3U"],
      ["x4u", "X4U"],
    ],
    "Hardware revisions can require different configurations. Check the label before provisioning.",
  ),
  ...models("fanvil", "Fanvil", "sip_phone", "fanvil-v", [["v65", "V65"]]),
  ...models("snom", "Snom", "sip_phone", "snom-d", [
    ["d713", "D713"],
    ["d735", "D735"],
    ["d785", "D785"],
  ]),
];

export function equipmentModelById(id: string | null | undefined) {
  return equipmentCatalog.find((entry) => entry.id === id);
}

export const equipmentIdentityInput = z
  .object({
    catalogModelId: z.string().trim().max(80).optional(),
    manufacturer: z.string().trim().max(40).optional(),
    model: z.string().trim().max(120).default(""),
    equipmentKind: equipmentKindSchema.optional(),
  })
  .superRefine((input, ctx) => {
    if (input.catalogModelId && input.catalogModelId !== "other") {
      if (!equipmentModelById(input.catalogModelId))
        ctx.addIssue({
          code: "custom",
          path: ["catalogModelId"],
          message: "Choose a listed model or Other.",
        });
      return;
    }
    if (!input.model)
      ctx.addIssue({
        code: "custom",
        path: ["model"],
        message: "Enter the equipment model.",
      });
    if (!input.equipmentKind)
      ctx.addIssue({
        code: "custom",
        path: ["equipmentKind"],
        message: "Choose the equipment type.",
      });
    if (input.catalogModelId === "other") {
      if (!input.manufacturer)
        ctx.addIssue({
          code: "custom",
          path: ["manufacturer"],
          message: "Enter the equipment brand.",
        });
      if (`${input.manufacturer ?? ""} ${input.model}`.length > 120)
        ctx.addIssue({
          code: "custom",
          path: ["model"],
          message: "Keep the brand and model together under 120 characters.",
        });
    }
  })
  .transform((input) => {
    const selected = equipmentModelById(input.catalogModelId);
    if (selected)
      return {
        catalogModelId: selected.id,
        manufacturer: selected.manufacturer,
        model: `${selected.manufacturer} ${selected.model}`,
        equipmentKind: selected.kind,
      };
    if (input.catalogModelId === "other")
      return {
        catalogModelId: null,
        manufacturer: input.manufacturer!,
        model: `${input.manufacturer} ${input.model}`,
        equipmentKind: input.equipmentKind!,
      };
    // Preserve legacy free-text requests and their idempotency hashes.
    return { model: input.model, equipmentKind: input.equipmentKind! };
  });

export function equipmentProvisioningIdentity(
  catalogModelId: string | null | undefined,
) {
  const entry = equipmentModelById(catalogModelId);
  return {
    catalogModelId: entry?.id ?? null,
    configurationFamily: entry?.configurationFamily ?? null,
    automaticSetup: "not_connected" as const,
    setupMethod: "manual_sip" as const,
    modelReviewRequired: !entry,
  };
}
